import type { Id } from '@/types/api/common'

/**
 * Every route path in the app. Never write a path literal in a link, redirect
 * or nav item — import from here so a rename is a single change.
 */
export const paths = {
  login: '/login',
  /** Second step of sign-in: the emailed one-time code (TURON-195). */
  loginVerifyOtp: '/login/verify-otp',
  forgotPassword: '/forgot-password',
  /** The reset email links here with `?token=…`. */
  resetPassword: '/reset-password',
  dashboard: '/',

  /**
   * The hotel-facing Secure Hotel Portal — a different audience from the rest
   * of this app, outside the staff shell and the staff session. The emailed
   * link opens `access(token)`; a verified session lands on `reservations`.
   */
  hotelPortal: {
    access: (token: string) => `/hotel-access/${token}`,
    reservations: '/hotel-portal',
  },

  customers: {
    list: '/customers',
    new: '/customers/new',
    /**
     * The edit form keeps `/customers/:id` so the grid's name link matches the
     * design; the read-only view sits beneath it. Swap the two here if the
     * detail screen should become the default landing.
     */
    edit: (id: Id) => `/customers/${id}`,
    detail: (id: Id) => `/customers/${id}/view`,
    types: '/customers/types',
  },

  events: {
    list: '/events',
    new: '/events/new',
    edit: (id: number | string) => `/events/${id}`,
    preview: (id: number | string) => `/events/${id}/preview`,
    /**
     * The read-only Event Overview and its sibling tabs, as the hotel's. Each
     * tab is a route so the breadcrumb and the browser's Back button follow it.
     */
    detail: (id: number | string) => `/events/${id}/overview`,
    hotels: (id: number | string) => `/events/${id}/hotels`,
    photos: (id: number | string) => `/events/${id}/photos`,
    logs: (id: number | string) => `/events/${id}/logs`,
  },

  hotels: {
    list: '/hotels',
    new: '/hotels/new',
    edit: (id: number | string) => `/hotels/${id}`,
    /** Edit Hotel opened on Content & Amenities, scrolled to Hotel Photos. */
    editPhotos: (id: number | string) => `/hotels/${id}?section=photos`,
    /**
     * The read-only Hotel Overview and its two sibling tabs. Each tab is a
     * route so the breadcrumb and the browser's Back button follow the tab.
     */
    detail: (id: number | string) => `/hotels/${id}/overview`,
    groups: (id: number | string) => `/hotels/${id}/events-groups`,
    photos: (id: number | string) => `/hotels/${id}/photos`,
    logs: (id: number | string) => `/hotels/${id}/logs`,
    roomTypes: (id: number | string) => `/hotels/${id}/room-types`,
  },

  /**
   * Room Inventory. The 2026-09-24 design gives Rooms a standalone listing and
   * a three-step Add / Edit wizard, so the module is no longer reached only
   * through an event-hotel group.
   */
  rooms: {
    list: '/rooms',
    new: '/rooms/new',
    edit: (id: number | string) => `/rooms/${id}`,
    /**
     * One room type's page and its tabs, each a route: Overview (the
     * read-only record, TURON-273), Room Type (edit), Dates (its inventory
     * across events and groups) and Logs.
     */
    roomType: (id: number | string) => `/rooms/room-types/${id}`,
    roomTypeDetails: (id: number | string) => `/rooms/room-types/${id}/details`,
    roomTypeDates: (id: number | string) => `/rooms/room-types/${id}/dates`,
    roomTypeLogs: (id: number | string) => `/rooms/room-types/${id}/logs`,
  },

  /**
   * The detail's five tabs are routes (Back and the trail follow them). Edit
   * is a modal on the detail (design 17): `edit(id)` opens it with `?edit=1`,
   * consumed once. `new` takes `?customerId=` (Customers → Add Reservation)
   * or `?waitlistId=` + router state (Waitlist → Convert).
   */
  reservations: {
    list: '/reservations',
    new: '/reservations/new',
    detail: (id: number | string) => `/reservations/${id}`,
    stay: (id: number | string) => `/reservations/${id}/stay`,
    guests: (id: number | string) => `/reservations/${id}/guests`,
    financials: (id: number | string) => `/reservations/${id}/financials`,
    activity: (id: number | string) => `/reservations/${id}/activity`,
    /** Edit Reservation is the Stay & Availability tab (the modal was removed). */
    edit: (id: number | string) => `/reservations/${id}/stay`,
  },

  /**
   * Edit is a side drawer on the detail screen (design 13), not a route:
   * `edit(id)` opens the detail with `?edit=1`, which the screen consumes once.
   * Convert is a modal there too, so it has no path.
   */
  waitlist: {
    list: '/waitlist',
    new: '/waitlist/new',
    detail: (id: number | string) => `/waitlist/${id}`,
    logs: (id: number | string) => `/waitlist/${id}/logs`,
    edit: (id: number | string) => `/waitlist/${id}?edit=1`,
  },

  transactions: {
    list: '/transactions',
    detail: (id: number | string) => `/transactions/${id}`,
  },

  commission: {
    list: '/commission',
  },

  /**
   * Secure Hotel Portal (`secure-link`). The detail's four tabs are routes, so
   * Back and the breadcrumb follow them. Send, Resend and Revoke are modals.
   */
  secureLinks: {
    list: '/secure-hotel-portal',
    new: '/secure-hotel-portal/new',
    detail: (id: number | string) => `/secure-hotel-portal/${id}`,
    reservations: (id: number | string) => `/secure-hotel-portal/${id}/reservations`,
    history: (id: number | string) => `/secure-hotel-portal/${id}/history`,
    activity: (id: number | string) => `/secure-hotel-portal/${id}/activity`,
  },

  /** `index` is the report picker; `report(type)` opens one report (`reportTypes.ts`). */
  reports: {
    index: '/reports',
    report: (type: string) => `/reports/${type}`,
  },

  settings: {
    index: '/settings',
    /**
     * LEGACY. Roles & Permissions used to live here; it moved under User
     * Management on 2026-09-15. Kept so `/settings/roles*` can redirect to its
     * new home — nothing else may link to it.
     */
    legacyRoles: '/settings/roles',
    emailTemplates: '/settings/email-templates',
    emailTemplate: (id: string) => `/settings/email-templates/${id}`,
    emailActivity: '/settings/email-activity',
    amenities: '/settings/amenities',
    additionalTypes: '/settings/additional-types',
    countries: '/settings/countries',
    regions: '/settings/regions',
    currencies: '/settings/currencies',
    systemMessages: '/settings/system-messages',
    officeHours: '/settings/office-hours',
  },

  cms: {
    list: '/website-content',
    /** A page is addressed by its fixed key (`about-us`), not a row id. */
    edit: (pageKey: string) => `/website-content/${pageKey}`,
    /** The page's preview on its own, full width — opened in a new tab from the editor. */
    preview: (pageKey: string) => `/website-content/${pageKey}/preview`,
    /** Not built — reserved for the §4 work (menus, footer, media library). */
    menus: '/website-content/menus',
    footer: '/website-content/footer',
    media: '/website-content/media',
  },

  /**
   * The sidebar's System > User Management container. It has no screen of its
   * own — `index` redirects to the first child — so both children live under
   * one base path and the submenu can tell it is the open section.
   */
  userManagement: {
    index: '/user-management',
    users: '/user-management/users',
    /** `new` is a static segment, so React Router ranks it above `role(:roleId)`. */
    roles: '/user-management/roles',
    roleNew: '/user-management/roles/new',
    role: (roleId: number | string) => `/user-management/roles/${roleId}`,
  },

  /**
   * LEGACY user screens, superseded by `userManagement` (2026-09-15). `list`
   * redirects to the new listing; the rest still render the older full-page
   * form and role screens and are reachable only by URL — see
   * `src/screens/users/agent.md` §23.1.
   */
  users: {
    list: '/users',
    new: '/users/new',
    edit: (id: number | string) => `/users/${id}`,
    roles: '/users/roles',
    permissions: (roleId: number | string) => `/users/roles/${roleId}/permissions`,
    profile: '/profile',
    preferences: '/profile/settings',
  },

  partners: {
    list: '/partners',
    edit: (id: number | string) => `/partners/${id}`,
  },

  audit: {
    trail: '/audit/trail',
    apiLogs: '/audit/api-logs',
  },

  forbidden: '/403',
} as const
